import { UploadOutlined, EyeOutlined } from "@ant-design/icons";
import { IonIcon } from "@ionic/react";
import { refreshOutline, pencilOutline } from 'ionicons/icons';
import { Button, Col, Row, Upload, notification } from "antd";
import { requestRetrace } from "../../../lib/firebase/retraceImage";
import { useContext, useState, useEffect } from "react";
import AppContext from "../../../contexts/AppContext";
import { useAuth } from "../../../contexts/AuthContext";
import "./SharpenImageForm.css";

const { useNotification } = notification;

const loadingSteps = [
  { text: "Analyzing your image structure...", progress: 15 },
  { text: "Detecting edges and boundaries...", progress: 30 },
  { text: "Enhancing contrast and clarity...", progress: 45 },
  { text: "Reconstructing fine details...", progress: 60 },
  { text: "Applying AI enhancement algorithms...", progress: 75 },
  { text: "Optimizing color accuracy...", progress: 85 },
  { text: "Finalizing high-resolution output...", progress: 95 },
  { text: "Almost ready...", progress: 98 },
];

const SharpenImageForm = ({ isModalVisible, setIsModalVisible }) => {
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [messageKey, setMessageKey] = useState(0);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [sharpenedImage, setSharpenedImage] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const { setImage64, setImageSource } = useContext(AppContext);
  const [api, contextHolder] = useNotification();
  const { user } = useAuth();

  const openNotification = (message, type, config = {}) => {
    api.open({
      message,
      duration: config.duration || 3,
      type,
      placement: "top",
      ...config,
    });
  };

  // Reset form when modal is closed
  useEffect(() => {
    if (!isModalVisible) {
      const timer = setTimeout(() => {
        resetForm();
      }, 300); // Delay to allow modal close animation
      return () => clearTimeout(timer);
    }
  }, [isModalVisible]);

  // Auto-sharpen when image is uploaded
  useEffect(() => {
    if (uploadedImage && !loading && !sharpenedImage) {
      sharpenImage();
    }
  }, [uploadedImage]);

  // rotate loading messages and progress while processing
  useEffect(() => {
    if (!loading) {
      setLoadingProgress(0);
      setLoadingMessage("");
      return;
    }
    
    const startTime = Date.now();
    const totalDuration = 15000; // 15 seconds (Nano Banana is fast)
    
    // Set initial message
    setLoadingMessage(loadingSteps[0].text);
    setLoadingProgress(0);
    setMessageKey(prev => prev + 1);
    
    // Smooth progress animation
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progressPercentage = Math.min((elapsed / totalDuration) * 98, 98);
      
      setLoadingProgress(progressPercentage);
      
      // Update message based on progress
      const stepIndex = Math.floor((progressPercentage / 98) * loadingSteps.length);
      const currentStep = loadingSteps[Math.min(stepIndex, loadingSteps.length - 1)];
      setLoadingMessage(currentStep.text);
      setMessageKey(prev => prev + 1);
    }, 500); // Update every 500ms for smooth animation
    
    return () => {
      clearInterval(progressInterval);
      setLoadingMessage("");
      setLoadingProgress(0);
      setMessageKey(0);
    };
  }, [loading]);

  const downloadImage = (imageData, filename) => {
    const link = document.createElement('a');
    link.href = imageData;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openInConverter = (imageData) => {
    setImage64(imageData);
    setImageSource("sharpened");
    setIsModalVisible(false);
  };

  const resetForm = () => {
    setShowComparison(false);
    setShowPreview(false);
    setOriginalImage(null);
    setSharpenedImage(null);
    setUploadedImage(null);
    setPreviewUrl(null);
  };

  const handleImageUpload = (file) => {
    // Validate file type
    const isValidType = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
    if (!isValidType) {
      openNotification('Please upload a JPG, PNG, or WebP image!', 'error');
      return false;
    }

    // Validate file size (max 10MB)
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      openNotification('Image must be smaller than 10MB!', 'error');
      return false;
    }

    // Convert to base64 for preview and processing
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1]; // Remove data:image prefix
      setUploadedImage(base64);
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    return false; // Prevent default upload behavior
  };

  const sharpenImage = async () => {
    if (!uploadedImage) {
      openNotification("Please upload an image first", "error");
      return;
    }

    setLoading(true);
    
    try {
      const resp = await requestRetrace(uploadedImage, user);

      const sharpenedImageData = "data:image/png;base64," + resp.data.base64;
      
      // Set progress to 100% when complete
      setLoadingProgress(100);
      
      // Store images for comparison
      setOriginalImage(previewUrl);
      setSharpenedImage(sharpenedImageData);
      
      // Show preview instead of auto-loading into converter
      setShowPreview(true);
      
    } catch (error) {
      console.error('Sharpen error:', error);
      
      if (error.response?.status === 401) {
        openNotification("Authentication failed. Please log in again.", "error");
      } else if (error.response?.status === 403) {
        openNotification("This feature requires a premium subscription.", "error");
      } else if (error.code === 'ECONNABORTED') {
        openNotification("Request timed out. The image might be too large.", "error");
      } else {
        openNotification(
          error.response?.data?.message || "Failed to sharpen image. Please try again.",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      
      <div
        style={{
          padding: "20px 20px 16px 20px",
          margin: "0",
          position: "relative",
          width: "100%",
          height: "auto",
          background: "linear-gradient(135deg, #FF6B9D 0%, #C66FBC 25%, #A8589C 50%, #8B4B8C 75%, #6E3F7C 100%)",
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(255, 107, 157, 0.25)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          overflow: "hidden",
        }}
      >
        {/* Subtle background overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)",
            pointerEvents: "none",
          }}
        />
        
        {!showComparison && !showPreview ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                marginBottom: "24px",
                width: "100%",
                position: "relative",
                zIndex: 1,
              }}
            >
              <IonIcon icon={refreshOutline} style={{ 
                fontSize: "28px", 
                color: "#ffffff",
                marginRight: "8px"
              }} />
              <h1 className="sharpen-title" style={{
                color: "#fff",
                fontFamily: "Clash Grotesk",
                fontSize: "28px",
                fontWeight: "500",
                lineHeight: "1.2",
                textAlign: "center",
                textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                margin: "0",
              }}>
                {loading ? "Tracing your image..." : "Retrace image"}
              </h1>
              <Button
                type="primary"
                size="small"
                style={{
                  borderRadius: "12px",
                  background: "linear-gradient(105deg, #00E9FF -71.56%, #FD90FF 131.26%)",
                  color: "#000",
                  fontWeight: "600",
                  border: "none",
                  padding: "4px 12px",
                  fontSize: "11px",
                  letterSpacing: "0.5px",
                }}
              >
                PRO
              </Button>
            </div>

            <div style={{
              textAlign: "center",
              marginBottom: "32px",
              position: "relative",
              zIndex: 1,
            }}>
              <p style={{
                fontSize: "16px",
                fontWeight: "400",
                color: "rgba(255, 255, 255, 0.9)",
                margin: "0 0 16px 0",
                lineHeight: "1.5",
              }}>
                Upload your image and AI will enhance its clarity and sharpness
              </p>
              <p style={{
                fontSize: "13px",
                fontWeight: "400",
                fontStyle: "italic",
                color: "rgba(255, 255, 255, 0.7)",
                margin: 0,
              }}>
                Processing usually takes 15-30 seconds
              </p>
            </div>

            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              flex: 1,
              position: "relative",
              zIndex: 1,
            }}>
              <Row align="middle" justify="center" style={{ width: "100%" }}>
                <Col span={20}>
                  <Upload.Dragger
                    name="file"
                    listType="picture-card"
                    beforeUpload={handleImageUpload}
                    showUploadList={false}
                    accept="image/jpeg,image/png,image/webp"
                    disabled={loading}
                    role="button"
                    aria-label="Upload image for enhancement"
                    aria-disabled={loading}
                    aria-busy={loading}
                    style={{
                      background: "rgba(255, 255, 255, 0.95)",
                      border: "2px dashed rgba(198, 111, 188, 0.4)",
                      borderRadius: "12px",
                      minHeight: "200px",
                      transition: "all 0.3s ease",
                    }}
                    className="sharpen-upload-dragger"
                  >
                      {previewUrl ? (
                        <div className="uploaded-image-preview" style={{ position: 'relative' }}>
                          <img 
                            src={previewUrl} 
                            alt="Selected image preview" 
                            style={{ 
                              maxWidth: '100%', 
                              maxHeight: '300px',
                              borderRadius: '8px',
                              opacity: loading ? 0.6 : 1,
                              transition: 'opacity 0.3s ease'
                            }} 
                          />
                          {loading ? (
                            <div className="simple-loading-overlay">
                              <div className="loading-content">
                                <div className="loading-text-overlay">
                                  <p className="loading-text" key={messageKey}>{loadingMessage}</p>
                                  <div className="progress-bar-container">
                                    <div 
                                      className="progress-bar-fill" 
                                      style={{ 
                                        width: `${loadingProgress}%`,
                                        transition: 'width 0.3s ease-out'
                                      }}
                                    ></div>
                                  </div>
                                  <div className="progress-percentage">{Math.round(loadingProgress)}%</div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="upload-overlay">
                              <p>Click or drag to replace</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="upload-placeholder">
                          <p className="ant-upload-drag-icon">
                            <UploadOutlined style={{ fontSize: '32px', color: '#C66FBC' }} />
                          </p>
                          <p className="ant-upload-text" style={{ color: '#333333', fontSize: '16px', fontWeight: '500' }}>Click or drag image to upload</p>
                          <p className="ant-upload-hint" style={{ color: '#666666' }}>
                            Supports JPG, PNG, or WebP (max 10MB)
                          </p>
                          <p className="ant-upload-hint" style={{ marginTop: '8px', fontWeight: 500, color: '#8B4B8C' }}>
                            AI enhancement starts automatically on upload
                          </p>
                        </div>
                      )}
                  </Upload.Dragger>
                </Col>
              </Row>
            </div>

            <div style={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              marginTop: "24px",
              flexWrap: "wrap",
              position: "relative",
              zIndex: 1,
            }}>
              <div style={{
                background: "rgba(255, 255, 255, 0.12)",
                color: "#ffffff",
                padding: "8px 16px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "600",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}>
                <span style={{ fontSize: "14px" }}>✨</span>
                <span>AI Enhanced</span>
              </div>
              <div style={{
                background: "rgba(255, 255, 255, 0.12)",
                color: "#ffffff",
                padding: "8px 16px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "600",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}>
                <span style={{ fontSize: "14px" }}>🎨</span>
                <span>High Quality</span>
              </div>
              <div style={{
                background: "rgba(255, 255, 255, 0.12)",
                color: "#ffffff",
                padding: "8px 16px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "600",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}>
                <span style={{ fontSize: "14px" }}>⚡</span>
                <span>Fast Processing</span>
              </div>
            </div>
          </>
        ) : showPreview ? (
          <div style={{
            padding: "32px",
            background: "linear-gradient(135deg, #FF6B9D 0%, #C66FBC 25%, #A8589C 50%, #8B4B8C 75%, #6E3F7C 100%)",
            borderRadius: "12px",
            position: "relative",
          }}>


            <div style={{
              display: "flex",
              justifyContent: "center",
              padding: "20px",
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: "12px",
              backdropFilter: "blur(10px)",
              position: "relative",
            }}>
              <img 
                src={sharpenedImage} 
                alt="Enhanced image preview" 
                style={{
                  maxWidth: "100%",
                  maxHeight: "400px",
                  borderRadius: "8px",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
                }}
              />
              
              {/* Preview text overlay on left */}
              <div style={{
                position: "absolute",
                top: "28px",
                left: "28px",
                color: "#ffffff",
                fontWeight: "700",
                fontSize: "16px",
                letterSpacing: "0.5px",
                zIndex: 10,
                background: "rgba(0, 0, 0, 0.5)",
                padding: "8px 16px",
                borderRadius: "8px",
                backdropFilter: "blur(8px)",
              }}>
                Sharpen Preview
              </div>
              
              {/* Overlay buttons in top right */}
              <div style={{
                position: "absolute",
                top: "28px",
                right: "28px",
                display: "flex",
                gap: "8px",
                zIndex: 10,
              }}>
                <Button 
                  size="small"
                  onClick={() => downloadImage(sharpenedImage, 'enhanced-image.png')}
                  aria-label="Download enhanced image"
                  title="Download enhanced image"
                  style={{
                    background: "rgba(255, 255, 255, 0.95)",
                    border: "none",
                    color: "#8B4B8C",
                    fontWeight: "600",
                    padding: "8px 16px",
                    height: "auto",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  Download
                </Button>
                <Button 
                  type="primary" 
                  size="small"
                  onClick={() => {
                    setImage64(sharpenedImage);
                    setImageSource("sharpened");
                    setIsModalVisible(false);
                  }}
                  aria-label="Open enhanced image in SVG Converter"
                  title="Open enhanced image in SVG Converter"
                  style={{
                    background: "linear-gradient(90deg, #FF6B9D 0%, #C66FBC 100%)",
                    border: "none",
                    color: "#ffffff",
                    fontWeight: "600",
                    padding: "8px 16px",
                    height: "auto",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(255, 107, 157, 0.4)",
                  }}
                >
                  Open in Converter
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{
            padding: "32px",
            background: "linear-gradient(135deg, #FF6B9D 0%, #C66FBC 25%, #A8589C 50%, #8B4B8C 75%, #6E3F7C 100%)",
            borderRadius: "12px",
            position: "relative",
          }}>
            <div style={{
              textAlign: "center",
              marginBottom: "24px",
            }}>
              <h2 style={{
                fontSize: "24px",
                fontWeight: "600",
                color: "#ffffff",
                margin: "0 0 8px 0",
              }}>Enhancement Results</h2>
              <p style={{
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.9)",
                margin: 0,
              }}>Compare your original image with the AI-enhanced version</p>
            </div>

            <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
              <Col span={12}>
                <div style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "12px",
                  padding: "16px",
                  backdropFilter: "blur(10px)",
                }}>
                  <h3 style={{
                    color: "#ffffff",
                    fontSize: "16px",
                    fontWeight: "600",
                    marginBottom: "12px",
                    textAlign: "center",
                  }}>Original</h3>
                  <img 
                    src={originalImage} 
                    alt="Original" 
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      marginBottom: "12px",
                    }}
                  />
                  <div style={{ textAlign: "center" }}>
                    <Button 
                      size="small" 
                      onClick={() => downloadImage(originalImage, 'original-image.png')}
                      style={{
                        background: "rgba(255, 255, 255, 0.15)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        color: "#ffffff",
                        borderRadius: "6px",
                      }}
                    >
                      Download Original
                    </Button>
                  </div>
                </div>
              </Col>
              
              <Col span={12}>
                <div style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "12px",
                  padding: "16px",
                  backdropFilter: "blur(10px)",
                }}>
                  <h3 style={{
                    color: "#ffffff",
                    fontSize: "16px",
                    fontWeight: "600",
                    marginBottom: "12px",
                    textAlign: "center",
                  }}>Enhanced</h3>
                  <img 
                    src={sharpenedImage} 
                    alt="Enhanced" 
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      marginBottom: "12px",
                    }}
                  />
                  <div style={{ textAlign: "center" }}>
                    <Button 
                      size="small" 
                      onClick={() => downloadImage(sharpenedImage, 'enhanced-image.png')}
                      style={{
                        background: "rgba(255, 255, 255, 0.15)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        color: "#ffffff",
                        borderRadius: "6px",
                      }}
                    >
                      Download Enhanced
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>

            <div style={{
              display: "flex",
              gap: "16px",
              justifyContent: "center",
            }}>
              <Button 
                type="primary" 
                size="large"
                onClick={() => setIsModalVisible(false)}
                style={{
                  background: "linear-gradient(90deg, #FF6B9D 0%, #C66FBC 100%)",
                  border: "none",
                  color: "#ffffff",
                  fontWeight: "600",
                  padding: "8px 24px",
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(255, 107, 157, 0.3)",
                }}
              >
                Back to Converter
              </Button>
              <Button 
                size="large"
                onClick={resetForm}
                style={{
                  background: "rgba(255, 255, 255, 0.15)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  color: "#ffffff",
                  fontWeight: "500",
                  padding: "8px 24px",
                  height: "auto",
                  borderRadius: "8px",
                  backdropFilter: "blur(10px)",
                }}
              >
                Enhance Another Image
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SharpenImageForm;
